import React, { useMemo, useState } from 'react'
import { Box, ButtonBase, Checkbox, InputAdornment, Skeleton, TextField, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Search as SearchIcon } from '@material-ui/icons'
import { useDebounce } from 'use-debounce'
import FileNameText from 'src/components/FileNameText'
import { Muted, Segmented, TextLink } from '../components/ui'
import { shortDay } from '../model/candidates'
import { PASTE_ID_LIMIT, isMultisampleType, useFinishedAnalyses, usePastedIds } from './useFinishedAnalyses'

// Finished analyses to pick for a multisample group: rows with a checkbox, the
// sample in Geist Mono, the workflow and date muted, and why a row cannot be picked.
// FinishedBrowser adds the search, the type chips and "Show more" around the rows;
// the empty Multisample area and FinishedPickerDialog both use it.

export const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'FASTQ', label: 'FASTQ' },
  { value: 'VCF', label: 'VCF' },
  { value: 'ONT_BAM', label: 'ONT' },
  { value: 'PACBIO_BAM', label: 'PacBio' },
  { value: 'Multisample', label: 'Multisample' },
]

const typeMatches = (row, type) =>
  type === 'all' || (type === 'Multisample' ? isMultisampleType(row.type) : row.type === type)

/** Why a finished-analyses row cannot be picked, or ''. */
export const finishedDisabledReason = (row, taken) => {
  if (taken && row.fileId && taken.has(String(row.fileId))) return 'In this group'
  if (!row.fileId) return 'No file'
  if (!row.done) return 'Running'
  return ''
}

const dayOf = (value) => {
  const t = new Date(value).getTime()
  return Number.isFinite(t) ? shortDay(t) : ''
}

const GRID = '36px minmax(0, 1fr) minmax(0, 1.25fr) 72px 96px'

const FinishedRow = ({ row, checked, reason, onToggle }) => {
  const theme = useTheme()
  const disabled = Boolean(reason)
  return (
    <ButtonBase
      component="div"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={row.key}
      onClick={() => !disabled && onToggle(row)}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: GRID,
        alignItems: 'center',
        columnGap: 2,
        minHeight: 44,
        px: 1,
        py: 0.5,
        borderRadius: `${theme.shape.borderRadius}px`,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': { backgroundColor: disabled ? 'transparent' : 'action.hover' },
      }}
    >
      <Checkbox
        size="small"
        checked={checked || reason === 'In this group'}
        disabled={disabled}
        tabIndex={-1}
        sx={{ p: 0.5 }}
        inputProps={{ 'aria-hidden': true }}
      />
      <Box sx={{ minWidth: 0, opacity: disabled ? 0.6 : 1 }}>
        <FileNameText name={row.key} variant="plain" maxLines={1} sx={{ fontSize: 14, fontWeight: 500 }} />
      </Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap title={row.summary}>
        {row.summary}
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }} noWrap>
        {dayOf(row.createdAt)}
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'right' }} noWrap>
        {reason}
      </Typography>
    </ButtonBase>
  )
}

/**
 * The rows. `selected` is a Map (or Set) keyed by fileId; `taken` the fileIds
 * already in the target group.
 */
const FinishedList = ({ rows, selected, taken, onToggle, isLoading, emptyText = 'No finished analyses match.' }) => {
  if (isLoading) {
    return Array.from({ length: 4 }).map((_, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <Skeleton key={i} variant="text" height={44} sx={{ mx: 1 }} />
    ))
  }
  if (!rows.length) return <Muted sx={{ px: 1, py: 3 }}>{emptyText}</Muted>
  return rows.map((row) => (
    <FinishedRow
      key={`${row.analysisId}:${row.fileId}`}
      row={row}
      checked={Boolean(row.fileId) && selected.has(row.fileId)}
      reason={finishedDisabledReason(row, taken)}
      onToggle={onToggle}
    />
  ))
}

// A search that names several samples (pasted from a sheet) is a list of IDs.
export const parseIdList = (text) => {
  const tokens = String(text || '')
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
  const unique = tokens.filter((t, i) => tokens.findIndex((u) => u.toLowerCase() === t.toLowerCase()) === i)
  return unique.length >= 2 ? unique : null
}

const PastedIdStatus = ({ ids, found, notFound, capped }) => (
  <Muted>
    {found} of {Math.min(ids.length, PASTE_ID_LIMIT)} found.
    {notFound.length ? ` Not found: ${notFound.slice(0, 8).join(', ')}${notFound.length > 8 ? ', …' : ''}.` : ''}
    {capped ? ` The first ${PASTE_ID_LIMIT} IDs are searched.` : ''}
  </Muted>
)

/**
 * Search, type chips and rows of finished analyses, with selection held by the
 * caller: `selected` Map(fileId -> row), `onToggle(row)`, `onSelectMany(rows)`.
 */
export const FinishedBrowser = ({ selected, taken, onToggle, onSelectMany, autoFocus, listSx }) => {
  const [search, setSearch] = useState('')
  const [type, setType] = useState('all')
  const idList = useMemo(() => parseIdList(search), [search])
  const [debounced] = useDebounce(idList ? '' : search, 300)
  const finished = useFinishedAnalyses({ search: debounced, enabled: !idList })
  const pasted = usePastedIds(idList || [], { enabled: Boolean(idList) })

  const rows = useMemo(() => {
    const source = idList ? pasted.matches : finished.rows
    return type === 'all' ? source : source.filter((r) => typeMatches(r, type))
  }, [idList, pasted.matches, finished.rows, type])

  const pickable = rows.filter((r) => !finishedDisabledReason(r, taken) && !selected.has(r.fileId))

  const onPaste = (e) => {
    const text = e.clipboardData?.getData('text') || ''
    if (!/[\r\n\t]/.test(text)) return
    e.preventDefault()
    setSearch(
      text
        .split(/[\r\n\t,;]+/)
        .map((t) => t.trim())
        .filter(Boolean)
        .join(', '),
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 1, pb: 1 }}>
        <TextField
          size="small"
          autoFocus={autoFocus}
          placeholder="Search, or paste sample IDs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search finished analyses', onPaste }}
          sx={{ width: 240, '& .MuiInputBase-root': { fontSize: 14 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Segmented value={type} options={TYPE_FILTERS} onChange={setType} fullWidth={false} size="small" />
      </Box>
      {idList && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1.5, px: 1, pb: 1 }}>
          {pasted.isLoading ? (
            <Muted>Looking up {Math.min(idList.length, PASTE_ID_LIMIT)} IDs</Muted>
          ) : (
            <PastedIdStatus
              ids={idList}
              found={pasted.matches.length}
              notFound={pasted.notFound}
              capped={pasted.capped}
            />
          )}
          {pickable.length > 0 && onSelectMany && (
            <TextLink onClick={() => onSelectMany(pickable)}>
              Select {pickable.length} {pickable.length === 1 ? 'match' : 'matches'}
            </TextLink>
          )}
        </Box>
      )}
      <Box sx={listSx}>
        <FinishedList
          rows={rows}
          selected={selected}
          taken={taken}
          onToggle={onToggle}
          isLoading={idList ? pasted.isLoading && !pasted.matches.length : finished.isLoading}
          emptyText={
            finished.isError && !idList ? 'Finished analyses could not be loaded.' : 'No finished analyses match.'
          }
        />
        {!idList && finished.hasMore && (
          <Box sx={{ px: 1, py: 1.5 }}>
            <TextLink onClick={finished.fetchMore} disabled={finished.isFetchingMore}>
              {finished.isFetchingMore ? 'Loading' : 'Show more'}
            </TextLink>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default FinishedList
